import { useEffect, useState } from 'react';
import { useParams, useNavigate, Navigate, Routes, Route } from 'react-router-dom';
import { 
  Edit2, 
  Play, 
  Settings, 
  LayoutDashboard, 
  PlaySquare, 
  UserPlus, 
  Plus, 
  ArrowUp, 
  ArrowDown,
  X,
  ArrowLeft
} from 'lucide-react';
import { useEngagements } from '../../hooks/useEngagements';
import { EngagementWithDetails } from '../../types/engagement';
import EditEngagementPopup from '../../components/engagements/EditEngagementPopup';
import SelectContactsPopup from '../../components/engagements/SelectContactsPopup';
import AddEngagementStepPopup from '../../components/engagements/AddEngagementStepPopup';
import ExecutingEngagementView from '../../components/engagements/ExecutingEngagementView';
import EngagementDashboard from '../../components/engagements/EngagementDashboard';
import EngagementContact from '../../components/engagements/EngagementContact';

export default function EngagementDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { 
    getEngagementDetails, 
    updateEngagement, 
    removeEngagementContact, 
    addEngagementContact,
    reorderEngagementStep,
    removeEngagementStep,
    addEngagementStep,
    moveContactToStep,
    deleteEngagement
  } = useEngagements();
  const [engagement, setEngagement] = useState<EngagementWithDetails | null>(null);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [showAddContactsModal, setShowAddContactsModal] = useState(false);
  const [showAddStepModal, setShowAddStepModal] = useState(false);
  const [isReorderingStep, setIsReorderingStep] = useState(false);

  const loadEngagement = async () => {
    if (!id) return;
    try {
      console.log('Loading engagement details for:', id);
      const data = await getEngagementDetails(id);
      console.log('Loaded engagement:', data);
      if (data) {
        setEngagement(data);
      } else {
        console.error('No engagement data received');
      }
    } catch (err) {
      console.error('Failed to load engagement:', err);
    }
  };

  useEffect(() => {
    loadEngagement();
  }, [id]);

  if (!engagement || !id) {
    return <div>Loading...</div>;
  }

  const isEditable = engagement.status !== 'stopped';

  const handleContactsSelected = async (selectedContacts: any[]) => {
    if (!id) return;
    
    try {
      // Get current contacts
      const currentContacts = engagement?.contacts || [];
      const currentContactIds = new Set(currentContacts.map(c => c.contact_id));
      
      // Find contacts to add and remove
      const selectedContactIds = new Set(selectedContacts.map(c => c.id));
      const contactsToAdd = selectedContacts.filter(c => !currentContactIds.has(c.id));
      const contactsToRemove = currentContacts.filter(c => !selectedContactIds.has(c.contact_id));
      
      // Remove contacts that are no longer selected
      for (const contact of contactsToRemove) {
        await removeEngagementContact(id, contact.contact_id);
      }
      
      // Add new contacts
      for (const contact of contactsToAdd) {
        await addEngagementContact(id, contact.id);
      }
      
      setShowAddContactsModal(false);
      await loadEngagement();
    } catch (err) {
      console.error('Failed to update engagement contacts:', err);
    }
  };

  return (
    <div className="min-h-screen bg-dark-900">


      <div className="container mx-auto">
        <div className="flex-1 p-6 space-y-6">
          {/* Title and Description */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <h1 className="text-2xl font-bold text-white">
                  {engagement.title}
                </h1>
                {engagement.status === 'active' && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => navigate(`/engagements/${id}/settings`)}
                      className="p-2 text-gray-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors"
                      title="Edit Engagement Details"
                    >
                      <Settings className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => navigate(`/engagements/${id}/dashboard`)}
                      className="p-2 text-gray-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors"
                      title="View Dashboard"
                    >
                      <LayoutDashboard className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => navigate(`/engagements/${id}/execute`)}
                      className="p-2 text-gray-400 hover:text-white hover:bg-dark-700 rounded-lg transition-colors"
                      title="View Execution"
                    >
                      <PlaySquare className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2">

                {isEditable && engagement.status === 'draft' && (
                  <>
                    <button
                      onClick={() => setShowEditPopup(true)}
                      className="btn btn-secondary"
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      Edit
                    </button>
                    <button
                      onClick={async () => {
                        if (!id || !window.confirm('Are you sure you want to delete this engagement? This action cannot be undone.')) return;
                        
                        try {
                          await deleteEngagement(id);
                          navigate('/engagements');
                        } catch (err) {
                          console.error('Failed to delete engagement:', err);
                          alert('Failed to delete engagement. Please try again.');
                        }
                      }}
                      className="btn bg-red-600 hover:bg-red-700 text-white"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Delete
                    </button>
                    <button 
                      onClick={async () => {
                        if (!engagement || !id) return;
                        try {
                          await updateEngagement(id, { status: 'active' });
                          await loadEngagement();
                        } catch (err) {
                          console.error('Failed to start engagement:', err);
                        }
                      }}
                      className="btn btn-primary"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Start Engagement
                    </button>
                  </>
                )}
              </div>
            </div>
            {engagement.description && (
              <p className="text-gray-400">{engagement.description}</p>
            )}
          </div>

          {/* Content */}
          {engagement.status === 'active' ? (
            <Routes>
              <Route 
                path="/" 
                element={<Navigate to="dashboard" replace />} 
              />
              <Route 
                path="execute" 
                element={
                  <ExecutingEngagementView
                    engagement={engagement}
                    onMoveContact={async (contactId, fromStepId, toStepId) => {
                      console.log('Moving contact in ExecutingEngagementView:', {
                        contactId,
                        fromStepId,
                        toStepId,
                        engagementId: id
                      });
                      const updatedEngagement = await moveContactToStep(id, contactId, toStepId);
                      if (!updatedEngagement) {
                        throw new Error('Failed to move contact: No engagement returned');
                      }
                      // Update parent state to keep dashboard in sync
                      setEngagement(updatedEngagement);
                      return updatedEngagement;
                    }}
                  />
                } 
              />
              <Route 
                path="dashboard" 
                element={<EngagementDashboard engagement={engagement} />} 
              />
              <Route 
                path="settings" 
                element={
                  <div className="space-y-6">
                    {/* Contacts Section */}
                    <div className="bg-dark-800 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium text-white">Contacts to Engage</h3>
                        <button
                          onClick={() => setShowAddContactsModal(true)}
                          className="btn btn-secondary btn-sm"
                        >
                          <UserPlus className="w-4 h-4 mr-2" />
                          Add Contacts
                        </button>
                      </div>
                      <div className="space-y-2">
                        {engagement.contacts.length === 0 ? (
                          <p className="text-gray-400 text-sm">No contacts added yet</p>
                        ) : (
                          engagement.contacts.map((contact) => (
                            <EngagementContact
                              key={contact.contact_id}
                              contactId={contact.contact_id}
                              onRemove={isEditable ? () => {
                                if (!id) return;
                                removeEngagementContact(id, contact.contact_id).then(loadEngagement);
                              } : undefined}
                            />
                          ))
                        )}
                      </div>
                    </div>

                    {/* Steps Section */}
                    <div className="bg-dark-800 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-medium text-white">Engagement Steps</h2>
                        {isEditable && (
                          <button 
                            onClick={() => setShowAddStepModal(true)}
                            className="btn btn-secondary"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Step
                          </button>
                        )}
                      </div>
                      {engagement.steps.length === 0 ? (
                        <div className="text-gray-400 text-sm">
                          No steps defined yet. Add steps to structure your engagement.
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {engagement.steps
                            .sort((a, b) => a.order_index - b.order_index)
                            .map((step, index) => (
                              <div 
                                key={step.id}
                                className="flex items-center justify-between p-4 bg-dark-700 rounded-lg"
                              >
                                <div className="flex items-center space-x-4">
                                  <span className="text-gray-400 font-medium">
                                    Step {index + 1}
                                  </span>
                                  <div>
                                    <h3 className="font-medium text-white">{step.name}</h3>
                                    {step.description && (
                                      <p className="text-sm text-gray-400 mt-1">{step.description}</p>
                                    )}
                                  </div>
                                </div>
                                {isEditable && (
                                  <div className="flex items-center space-x-2">
                                    {index > 0 && (
                                      <button
                                        onClick={async () => {
                                          if (!id || isReorderingStep) return;
                                          setIsReorderingStep(true);
                                          try {
                                            await reorderEngagementStep(id, step.id, index - 1);
                                            await loadEngagement();
                                          } catch (err) {
                                            console.error('Failed to move step up:', err);
                                          } finally {
                                            setIsReorderingStep(false);
                                          }
                                        }}
                                        className="p-1 text-gray-400 hover:text-white disabled:opacity-50"
                                        disabled={isReorderingStep}
                                        title="Move Up"
                                      >
                                        <ArrowUp className="w-4 h-4" />
                                      </button>
                                    )}
                                    {index < engagement.steps.length - 1 && (
                                      <button
                                        onClick={async () => {
                                          if (!id || isReorderingStep) return;
                                          setIsReorderingStep(true);
                                          try {
                                            await reorderEngagementStep(id, step.id, index + 1);
                                            await loadEngagement();
                                          } catch (err) {
                                            console.error('Failed to move step down:', err);
                                          } finally {
                                            setIsReorderingStep(false);
                                          }
                                        }}
                                        className="p-1 text-gray-400 hover:text-white disabled:opacity-50"
                                        disabled={isReorderingStep}
                                        title="Move Down"
                                      >
                                        <ArrowDown className="w-4 h-4" />
                                      </button>
                                    )}
                                    <button
                                      onClick={async () => {
                                        if (!id) return;
                                        if (!window.confirm('Are you sure you want to remove this step?')) return;
                                        try {
                                          await removeEngagementStep(id, step.id);
                                          await loadEngagement();
                                        } catch (err) {
                                          console.error('Failed to remove step:', err);
                                        }
                                      }}
                                      className="p-1 text-gray-400 hover:text-red-400"
                                      title="Remove Step"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                } 
              />
              <Route 
                path="" 
                element={<Navigate to="execute" replace />} 
              />
            </Routes>
          ) : (
            // Draft mode - show settings view
            <>
              {/* Contacts Section */}
              <div className="bg-dark-800 rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-white">Contacts to Engage</h3>
                  <button
                    onClick={() => setShowAddContactsModal(true)}
                    className="btn btn-secondary btn-sm"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Contacts
                  </button>
                </div>
                <div className="space-y-2">
                  {engagement.contacts.length === 0 ? (
                    <p className="text-gray-400 text-sm">No contacts added yet</p>
                  ) : (
                    engagement.contacts.map((contact) => (
                      <EngagementContact
                        key={contact.contact_id}
                        contactId={contact.contact_id}
                        onRemove={isEditable ? () => {
                          if (!id) return;
                          removeEngagementContact(id, contact.contact_id).then(loadEngagement);
                        } : undefined}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* Steps Section */}
              <div className="bg-dark-800 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-white">Engagement Steps</h2>
                  {isEditable && (
                    <button 
                      onClick={() => setShowAddStepModal(true)}
                      className="btn btn-secondary"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Step
                    </button>
                  )}
                </div>
                {engagement.steps.length === 0 ? (
                  <div className="text-gray-400 text-sm">
                    No steps defined yet. Add steps to structure your engagement.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {engagement.steps
                      .sort((a, b) => a.order_index - b.order_index)
                      .map((step, index) => (
                        <div 
                          key={step.id}
                          className="flex items-center justify-between p-4 bg-dark-700 rounded-lg"
                        >
                          <div className="flex items-center space-x-4">
                            <span className="text-gray-400 font-medium">
                              Step {index + 1}
                            </span>
                            <div>
                              <h3 className="font-medium text-white">{step.name}</h3>
                              {step.description && (
                                <p className="text-sm text-gray-400 mt-1">{step.description}</p>
                              )}
                            </div>
                          </div>
                          {isEditable && (
                            <div className="flex items-center space-x-2">
                              {index > 0 && (
                                <button
                                  onClick={async () => {
                                    if (!id || isReorderingStep) return;
                                    setIsReorderingStep(true);
                                    try {
                                      await reorderEngagementStep(id, step.id, index - 1);
                                      await loadEngagement();
                                    } catch (err) {
                                      console.error('Failed to move step up:', err);
                                    } finally {
                                      setIsReorderingStep(false);
                                    }
                                  }}
                                  className="p-1 text-gray-400 hover:text-white disabled:opacity-50"
                                  disabled={isReorderingStep}
                                  title="Move Up"
                                >
                                  <ArrowUp className="w-4 h-4" />
                                </button>
                              )}
                              {index < engagement.steps.length - 1 && (
                                <button
                                  onClick={async () => {
                                    if (!id || isReorderingStep) return;
                                    setIsReorderingStep(true);
                                    try {
                                      await reorderEngagementStep(id, step.id, index + 1);
                                      await loadEngagement();
                                    } catch (err) {
                                      console.error('Failed to move step down:', err);
                                    } finally {
                                      setIsReorderingStep(false);
                                    }
                                  }}
                                  className="p-1 text-gray-400 hover:text-white disabled:opacity-50"
                                  disabled={isReorderingStep}
                                  title="Move Down"
                                >
                                  <ArrowDown className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={async () => {
                                  if (!id) return;
                                  if (!window.confirm('Are you sure you want to remove this step?')) return;
                                  try {
                                    await removeEngagementStep(id, step.id);
                                    await loadEngagement();
                                  } catch (err) {
                                    console.error('Failed to remove step:', err);
                                  }
                                }}
                                className="p-1 text-gray-400 hover:text-red-400"
                                title="Remove Step"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      {showEditPopup && engagement && (
        <EditEngagementPopup
          engagement={engagement}
          onClose={() => setShowEditPopup(false)}
          onSuccess={() => {
            loadEngagement();
            setShowEditPopup(false);
          }}
        />
      )}

      {showAddContactsModal && engagement && (
        <SelectContactsPopup
          currentlySelectedIds={engagement.contacts.map(c => c.contact_id)}
          onClose={() => setShowAddContactsModal(false)}
          onSave={handleContactsSelected}
        />
      )}

      {showAddStepModal && engagement && (
        <AddEngagementStepPopup
          currentStepCount={engagement.steps.length}
          onClose={() => setShowAddStepModal(false)}
          onSave={async (data) => {
            if (!id) return;
            try {
              await addEngagementStep(id, data);
              await loadEngagement();
              setShowAddStepModal(false);
            } catch (err) {
              console.error('Failed to add step:', err);
            }
          }}
        />
      )}
    </div>
  );
}
